/* ============================================================
   VEYLA | listicle E, "the hairstylist"

   The supplied advertorial script, run as a page that argues and then routes.
   Three jobs, in this order:

     1. PAINT THE PROOF. Every unverifiable string on this page comes from
        vs-fiction.js and nowhere else. This file reads VS_FICTION.live and
        never the raw branches, so the OFF switch cannot be half applied, and
        it holds no invented sentence of its own. The strings it DOES own are
        disclosures, which are ours, are always true, and must survive OFF.

     2. THE LEDGER. She names what she was about to spend on the alternatives.
        That number is converted into cover on the current offer: how long it
        lasts, and what is left in her pocket afterwards. The arithmetic is the
        page's argument, and it is the whole of what the box does.

     3. ROUTING, NOT SELLING. Omar's call, 2026-08-12. No ladder, no bars, no
        subscribe toggle, no cart. The offer is named once and every CTA goes
        to the CLIENT'S OWN product page (Omar, 2026-08-12). No price is typed
        in this file: the figures come from vs-destination.js, which describes
        what that page charges. Read its header before touching the sums.

   PRICES. window.VY_SURFACE is set by the theme layout before the offer
   module loads, so the ladder here is the one the destination actually
   charges. Off the theme the module's own default applies.
   ============================================================ */
(() => {
  const O = window.VY_OFFER;
  if (!O) return;
  const F = (window.VS_FICTION && window.VS_FICTION.live) || null;

  const $  = s => document.querySelector(s);
  const $$ = s => Array.prototype.slice.call(document.querySelectorAll(s));
  const money = O.money;
  const esc = s => String(s).replace(/[&<>"]/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* Images this file applies rather than the markup: the persona's avatar and
     portrait. On the Shopify theme they live in a flat assets/ folder under a
     vs- prefix and are addressed by CDN url, so the layout emits a map from
     local filename to that url. Off the theme the map is absent and the path
     in the register is already right. */
  const img = p => (window.VS_IMG && window.VS_IMG[String(p).split('/').pop()]) || p;

  /* Four-figure sums, which no other Veyla surface has ever had to print.
     VY_OFFER.money has no thousands separator because every price it has ever
     formatted is under a thousand dollars, and it is shared with the PDP and
     the homepage, so this page does not go changing it. The ledger's own
     amounts are the only ones big enough to need the comma, and "$4000.00" in
     a box whose entire job is arithmetic reads like a typo. Left alone if the
     currency bridge is on and has already grouped the digits. */
  const sum = c => {
    const s = money(c);
    return s.indexOf(',') !== -1 ? s : s.replace(/(\d)(?=(\d{3})+(\.|$))/g, '$1,');
  };

  /* Four weeks of cover per tube. The one product fact this file holds, and it
     is the same figure the other landers' supply maths uses. */
  const WEEKS_PER_TUBE = 4;
  /* A full read of whether it is working. Twelve weeks, which is the window
     the diary lander is built on and the one the reviews keep landing in. */
  const READ_WEEKS = 12;

  /* ============================================================
     PART ONE: the proof layer
     ============================================================ */

  const setF = (key, value) =>
    $$('[data-f="' + key + '"]').forEach(el => { el.textContent = value; });

  const initials = name => String(name).split(/\s+/).filter(Boolean)
    .slice(0, 2).map(w => w[0].toUpperCase()).join('');

  const paintProof = () => {
    if (!F) return;
    const s = F.stylist;

    setF('pub', F.byline.publication);
    setF('byline-desk', F.byline.desk);
    setF('stylist-name', s.name);
    setF('stylist-name-inline', s.name);
    setF('stylist-role', s.age ? s.role + ', ' + s.age : s.role);
    setF('stylist-cite', s.name);
    setF('stylist-quote1', s.quote1);
    setF('stylist-quote2', s.quote2);
    setF('stylist-mono', initials(s.name));

    /* The face is the register's decision, not this file's: whether a face may
       appear at all depends on whose face it is. Both branches currently carry
       none, so the byline runs on initials and this stays a no-op until a
       generated face or a real, named hairstylist exists. */
    const face = $('#vs-byline-face');
    if (s.avatar && face) { face.src = img(s.avatar); face.hidden = false;
                            $$('[data-f="stylist-mono"]').forEach(el => el.hidden = true); }

    /* The hero frame belongs to the BRAND'S campaign image, not to the
       persona, and the caption says so. The persona's own portrait slot is
       still honoured first if one is ever supplied, because a page that
       silently ignored it would hide the more dangerous case. */
    const port = $('#vs-hero-portrait');
    const creative = (F.clientSourced && F.clientSourced.creative) || null;
    if (port) {
      if (s.portrait) {
        port.src = img(s.portrait); port.alt = ''; port.hidden = false;
        setF('portrait-cap', 'Illustrative portrait. Not a photograph of an identifiable person.');
      } else if (creative && creative.ritual) {
        port.src = img(creative.ritual.src); port.alt = creative.ritual.alt; port.hidden = false;
        setF('portrait-cap', creative.ritual.cap);
      } else {
        setF('portrait-cap', '');
      }
    }

    /* The brand's diagram and the brand's guarantee shot. Each one is hidden
       unless the register supplies it, and each one renders WITH its caption
       in the same breath, so nobody can end up with the brand's mechanism
       drawing on the page under no attribution at all. */
    const figure = (sel, item) => {
      const el = $(sel);
      if (!el || !item) return;
      el.querySelector('img').src = img(item.src);
      el.querySelector('img').alt = item.alt;
      el.querySelector('figcaption').textContent = item.cap;
      el.hidden = false;
    };
    if (creative) {
      figure('#vs-diagram', creative.science);
      figure('#vs-promise', creative.promise);

      const proof = $('#vs-proof');
      const grid = proof && proof.querySelector('.vs-proof-grid');
      if (grid && creative.beforeAfter && creative.beforeAfter.length) {
        grid.innerHTML = creative.beforeAfter.map(b =>
          `<img src="${esc(img(b.src))}" alt="${esc(b.alt)}" loading="lazy">`).join('');
        proof.querySelector('.vs-proof-note').textContent = creative.beforeAfterNote;
        proof.hidden = false;
      }
    }

    /* The outside options, printed as ranges rather than as the single figure
       the ledger computes on, because a range is what is actually known. */
    const out = F.outside;
    const byKey = k => out.options.filter(o => o.key === k)[0];
    setF('cost-transplant', byKey('transplant').rangeNote.replace(/^commonly /, ''));
    setF('cost-minoxidil',  byKey('minoxidil').rangeNote.replace(/^commonly /, ''));
    setF('outside-note', out.note);

    /* The brand's own claim, attributed to the brand. */
    const cs = F.clientSourced && F.clientSourced.nanoExosome;
    if (cs) {
      $$('[data-cs="figure"]').forEach(el => { el.textContent = cs.figure; });
      $$('[data-cs="claim"]').forEach(el => { el.textContent = cs.claim; });
      $$('[data-cs="note"]').forEach(el => { el.textContent = cs.note; });
    } else {
      $$('[data-cs]').forEach(el => { if (el.hasAttribute('data-cs') && el.tagName === 'ASIDE') el.remove(); });
    }

    /* Whole sections that exist only because the persona does. Removed rather
       than emptied: a heading over nothing is worse than no heading. */
    if (!F.invented) {
      $$('[data-fiction-section]').forEach(el => el.remove());
    } else {
      if (F.salonResults) setF('salon-body', F.salonResults.body);
      setF('virality', F.virality || '');
      /* Ours. The account above is one person's story and the page says so in
         its own voice, not in hers. */
      setF('illus-note', 'This account is illustrative and is not a clinical result. Individual experience varies, and nothing on this page has been evaluated as a treatment.');

      const list = $('#vs-reflist');
      if (list && F.references.length) {
        list.innerHTML = F.references.map(r => '<li>' + r + '</li>').join('');
        setF('ref-note', 'These citations are illustrative and are not linkable sources. They are carried from the supplied script and should be replaced with real references or removed before this page is treated as evidence of anything.');
      } else if (list) {
        const sec = $('#vs-refs'); if (sec) sec.remove();
      }
    }

    /* THE FOOTER IS A SIMPLE BRAND FOOTER NOW (Omar, 2026-08-12), and this is
       the single line that survived the cut. One sentence, in both branches,
       carrying the three things this page cannot be honest without: that it is
       an advertorial, that its byline and its citations are illustrative, and
       that hair loss is not something a page sells you out of.

       It is ours, not the register's, which is why it is written here. If it
       is ever deleted, the persona, the portrait and the citations have to go
       with it: those are the things it is disclosing. */
    setF('foot-fiction', F.invented
      ? 'Advertorial published by Veyla. The hairstylist, her portrait and the references are illustrative, not records of real people. Customer reviews are real. Not medical advice.'
      : 'Advertorial published by Veyla. Customer reviews are real and reproduced as written. Not medical advice.');

    /* Reviews. Real in both branches, so they are never gated.

       THE PORTRAITS ARE NOT THE REVIEWERS. These five people wrote these five
       reviews; the faces beside them are generated and photograph nobody. That
       pairing is only honest while the strip says so, so the note is written
       from the register in the same pass that draws the faces, and the harness
       fails if faces appear without it. A review with no avatar keeps its
       monogram, which is what the strip looked like before today. */
    const revNote = $('.vs-rev-note');
    if (revNote && F.portraitNote && F.reviews && F.reviews.some(r => r.avatar)) {
      revNote.textContent = F.portraitNote;
    }
    const revs = $('#vs-revs');
    if (revs && F.reviews) {
      revs.innerHTML = F.reviews.map(r => `
        <figure class="vs-rev">
          <div class="vs-rev-top">
            ${r.avatar
              ? `<img class="vs-rev-face" src="${esc(img(r.avatar))}" alt="" loading="lazy">`
              : `<span class="vs-rev-mono" aria-hidden="true">${esc(initials(r.name))}</span>`}
            <div>
              <b>${esc(r.name)}</b>
              <small>${esc(r.tag)}</small>
            </div>
            <span class="vs-rev-stars" aria-label="${r.stars} out of 5">${'★'.repeat(r.stars)}<i>${'★'.repeat(5 - r.stars)}</i></span>
          </div>
          <blockquote>${esc(r.body)}</blockquote>
        </figure>`).join('');
    }
  };

  /* The one Veyla figure in the comparison table, computed rather than typed.

     The supplied script's row said "Under $50 (One-time purchase)". This
     prints what a tube costs on the page the buttons open, which is the only
     price this lander is entitled to quote. No subscribed price, no
     compare-at, no saving: the page does not announce the offer. */
  const paintOffer = () => {
    $$('[data-o="cost-veyla"]').forEach(el => {
      el.textContent = money(basisCost()) + ', one-time';
    });
  };

  /* ============================================================
     PART TWO: the ledger
     ============================================================ */

  const state = {
    source: null,        /* which outside option is selected, or null */
    cents:  0,           /* the number the arithmetic runs on */
    touched: false       /* has she picked or typed a number yet */
  };

  /* THE BASIS FOR THE ARITHMETIC.

     Every CTA on this page opens the CLIENT'S product page, so every figure
     the ledger prints has to be true of THAT page. vs-destination.js holds
     what it costs there, read off the store itself and dated. Read its header
     before touching any of this: it explains why the basis is the dearest way
     to buy rather than the cheapest.

     It falls back to our own ladder only if the destination file is missing,
     which would mean the build is broken rather than the page is fine. */
  const D = window.VS_DEST || null;

  const basisCost = () => D ? D.basisCents
    : O.tierTotal(O.TIER_ORDER.slice().sort((a, b) =>
        O.perTube(a, O.SUB_DEFAULT) - O.perTube(b, O.SUB_DEFAULT))[0], O.SUB_DEFAULT);

  /* Tubes per order. One, on the destination: that product has a single
     variant, and its multi-tube offers are priced by an app in the cart, so
     there is nothing on it we could honestly read a bundle out of. */
  const basisTubes = () => {
    if (D) return 1;
    const k = O.TIER_ORDER.slice().sort((a, b) =>
      O.perTube(a, O.SUB_DEFAULT) - O.perTube(b, O.SUB_DEFAULT))[0];
    return O.TIERS[k].tubes;
  };

  /* The per tube list price the store itself publishes for that product. */
  const rrpCents = () => {
    if (D) return D.compareCents;
    const t = O.TIERS[O.TIER_ORDER[0]];
    return Math.round(t.compareCents / t.tubes);
  };

  const destUrl = () => (D && D.url) || O.PRODUCT_URL;

  const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);

  /* What her number buys on the current offer, at the destination's price.
     Whole orders only: half an order is not a thing anyone can buy. */
  const ledger = () => {
    const cost = basisCost();
    const orders = cost > 0 ? Math.floor(state.cents / cost) : 0;
    const tubes = orders * basisTubes();
    const weeks = tubes * WEEKS_PER_TUBE;
    return { cost, orders, tubes, weeks, left: state.cents - orders * cost };
  };

  const spanOf = weeks => {
    const years = weeks / 52;
    if (years >= 10) return { n: String(Math.floor(years)), unit: 'years' };
    if (weeks >= 52) return { n: (Math.floor(years * 10) / 10).toFixed(1), unit: 'years' };
    if (weeks >= 8)  return { n: String(Math.floor(weeks / 4)), unit: 'months' };
    return { n: String(weeks), unit: weeks === 1 ? 'week' : 'weeks' };
  };

  const paintOut = () => {
    const box = $('#vs-out');
    if (!box) return;

    if (!state.touched || state.cents <= 0) {
      box.className = 'vs-ledger-out is-empty';
      box.innerHTML = `
        <p class="vs-out-lead">Pick one above, or type what you have already spent.</p>
        <p class="vs-out-sub">Whatever the number is, this works out how long it covers here and what is left over.</p>`;
      return;
    }

    const l = ledger();
    const span = spanOf(l.weeks);
    const src = state.source ? F.outside.options.filter(o => o.key === state.source)[0] : null;

    /* ONE LINE. Four cells, read left to right as a sentence: what she named,
       how long it covers, what that is in tubes and weeks, and what she still
       has. It used to be a tall stacked panel, which gave four short figures
       the footprint of a paragraph.

       NO PRICE, NO LADDER, NO TIER NAME. The page names the offer once, under
       the button, and the product page is where the numbers live. */
    box.className = 'vs-ledger-out';
    box.innerHTML = l.orders > 0 ? `
      <div class="vs-out-line">
        <div class="vs-out-cell vs-out-named">
          <b>${sum(state.cents)}</b>
          <span>${src ? esc(src.label.toLowerCase()) : 'your number'}</span>
        </div>
        <div class="vs-out-cell vs-out-span">
          <b>${span.n} <em>${span.unit}</em></b>
          <span>of cover on the current offer</span>
        </div>
        <div class="vs-out-cell">
          <b>${l.tubes} <em>tubes</em></b>
          <span>${l.weeks} weeks, four to a tube</span>
        </div>
        <div class="vs-out-cell vs-out-left">
          <b>${sum(state.cents - l.cost)}</b>
          <span>still yours after the first order</span>
        </div>
      </div>
      <p class="vs-out-rec">${l.weeks >= READ_WEEKS
        ? 'That is past the ' + READ_WEEKS + ' weeks it takes to tell whether it is working, with the change left over.'
        : 'Short of the ' + READ_WEEKS + ' weeks it takes to tell whether it is working.'}</p>`
    : `
      <div class="vs-out-line vs-out-short">
        <div class="vs-out-cell vs-out-named">
          <b>${sum(state.cents)}</b>
          <span>${src ? esc(src.label.toLowerCase()) : 'your number'}</span>
        </div>
        <div class="vs-out-cell">
          <b>Under the current offer</b>
          <span>Sizes, prices and availability are on the product page.</span>
        </div>
      </div>`;
  };

  /* ============================================================
     PART THREE: routing, not selling

     Omar's call, 2026-08-12: this lander does not announce the offer. No bars,
     no compare-at, no subscribe toggle, no add to cart. It names the saving
     once and sends her to the product page, which is where price, sizes and
     availability actually live and the only place they can never be stale.

     That also deletes a whole class of failure this page used to carry: two
     price ladders on one theme, one of them a build behind (see #883 and the
     note in build-listicle-theme.py). A router cannot misprice anything.
     ============================================================ */

  const paintRoute = () => {
    /* Every CTA on the page resolves through the offer module rather than a
       typed href, because the client renamed this product once already and the
       handle is not a stable key. */
    $$('[data-pdp]').forEach(a => { a.setAttribute('href', destUrl()); });

    /* The list price the store publishes for a tube of the product the button
       opens, so the fine print can name it without anybody typing a number. */
    $$('[data-rrp]').forEach(el => { el.textContent = money(rrpCents()); });

    const sticky = $('#vs-sticky-txt');
    if (sticky) sticky.textContent = 'One minute a day, on the scalp';
  };

  /* The three things she can compare against. Painted from the register, so
     the client can change what the alternatives are without touching code. */
  const paintOpts = () => {
    const wrap = $('#vs-opts');
    if (!wrap || !F) return;
    wrap.innerHTML = F.outside.options.map(o => `
      <button type="button" class="vs-opt${state.source === o.key ? ' is-on' : ''}"
              role="radio" aria-checked="${state.source === o.key}" data-opt="${o.key}">
        <b>${esc(o.label)}</b>
        <span>${esc(o.rangeNote)}</span>
      </button>`).join('');
  };

  const paint = () => { paintOpts(); paintOut(); };

  const setAmountField = cents => {
    const f = $('#vs-amt');
    if (f) f.value = cents > 0 ? (cents / 100).toFixed(2).replace(/\.00$/, '') : '';
  };

  /* ---------- events ---------- */

  const onOpt = key => {
    const o = F.outside.options.filter(x => x.key === key)[0];
    state.source = key;
    if (o.cents > 0) { state.cents = o.cents; state.touched = true; setAmountField(o.cents); }
    else { state.cents = 0; state.touched = true; setAmountField(0); const f = $('#vs-amt'); if (f) f.focus(); }
    paint();
  };

  document.addEventListener('click', e => {
    const opt = e.target.closest && e.target.closest('[data-opt]');
    if (opt) { onOpt(opt.dataset.opt); return; }

    const jump = e.target.closest && e.target.closest('[data-jump]');
    if (jump) { const el = $('#vs-ledger'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  });

  const amt = $('#vs-amt');
  if (amt) amt.addEventListener('input', () => {
    const n = parseFloat(String(amt.value).replace(/[^0-9.]/g, ''));
    state.cents = isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
    state.touched = true;
    paintOut();
  });

  /* ============================================================
     THE SCROLL-LOCK GUARD

     Found live on 2026-08-12: the page would simply stop scrolling.

     The store runs Kaching Popup, which arrives with {{ content_for_header }}
     like every other app embed. Its script does this when it opens:

         lockBodyScroll() { document.body.style.overflow = 'hidden' }

     The layout hides that popup, because a bundle popup fighting this page's
     own offer is a stray. So it opens INVISIBLY on its trigger, takes the
     scroll, and leaves the reader with a frozen page and nothing on screen to
     close. The Klaviyo override above is the same bug caught earlier, but it
     only matches a CLASS; this one is an inline style, so nothing in CSS can
     reach it.

     Two defences, because either alone leaves a hole:

       1. Delete the popup element whenever it appears. No popup, no lock, and
          no invisible thing listening for exit intent on a page it was never
          meant to run on.
       2. Watch the body's inline style anyway and release any lock this page
          did not ask for. That one is deliberately generic: the next app the
          client installs will do exactly the same thing, and it will do it
          without telling anybody.

     OUR OWN drawer locks the body the same way, on purpose, so the guard
     checks whether it is open before releasing. Otherwise the page would
     scroll behind an open cart. */
  const drawerOpen = () => !!document.querySelector('.vyd.is-open, .vyd-scrim.is-open');

  const releaseScroll = () => {
    if (drawerOpen()) return;
    const b = document.body, h = document.documentElement;
    if (b.style.overflow === 'hidden') b.style.overflow = '';
    if (h.style.overflow === 'hidden') h.style.overflow = '';
    /* the iOS variant: body pinned with a negative top instead of hidden */
    if (b.style.position === 'fixed') { b.style.position = ''; b.style.top = ''; b.style.width = ''; }
  };

  const killStrayPopups = root => {
    const nodes = (root || document).querySelectorAll
      ? (root || document).querySelectorAll('kaching-popup-v2, #kaching-popup-v2-root')
      : [];
    Array.prototype.forEach.call(nodes, n => n.remove());
  };

  if (window.MutationObserver) {
    killStrayPopups();
    releaseScroll();
    new MutationObserver(records => {
      let sawNode = false;
      records.forEach(r => { if (r.type === 'childList' && r.addedNodes.length) sawNode = true; });
      if (sawNode) killStrayPopups();
      releaseScroll();
    }).observe(document.documentElement, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class']
    });
  }

  /* ---------- sticky ---------- */
  const sticky = $('#vs-sticky');
  const box = $('#vs-ledger');
  if (sticky && box && 'IntersectionObserver' in window) {
    /* Visible once the reader is past the fold, hidden again while the ledger
       itself is on screen, because two calls to action fighting each other on
       a phone is how the last one of these lost its footer. */
    new IntersectionObserver(es => {
      es.forEach(e => { sticky.hidden = e.isIntersecting || window.scrollY < 600; });
    }, { rootMargin: '0px 0px -40% 0px' }).observe(box);
    window.addEventListener('scroll', () => {
      if (window.scrollY < 600) sticky.hidden = true;
    }, { passive: true });
  }

  paintProof();
  paintOffer();
  paintRoute();
  paint();

  /* Read by the harness, so the arithmetic can be driven without a browser. */
  window.VS = { state, ledger, spanOf, paint, basisCost, basisTubes, rrpCents, destUrl, WEEKS_PER_TUBE, READ_WEEKS };
})();
